import { Paintbrush2, Check } from "lucide-react";

/**
 * Gate 2 · Surface-Based Editing (LOCKED 2026-02).
 * LF-007 · UX guardrail (2026-02-24): the panel must NEVER conflict with
 * the part-chip state. If the customer has already coloured N parts but
 * nothing is currently in the editing set, we surface progress instead of
 * a misleading "tap a part above" prompt.
 *
 * States:
 *   - editing set > 0    → "You're colouring: <parts>" + count line + current colour swatch
 *   - editing set 0, no colours anywhere → "Tap a part of your home above to start colouring."
 *   - editing set 0, some parts already coloured → progress line + gentle nudge to Render Scheme
 */
export default function EditingPanel({ selectedParts, activeArea, areas, currentColour, assignments }) {
  const activeAreaObj = (areas || []).find((a) => a.slug === activeArea) || null;
  const partsCatalog = activeAreaObj?.parts || [];

  const rows = selectedParts
    .map((slug) => partsCatalog.find((p) => p.slug === slug))
    .filter(Boolean);
  const editingEmpty = rows.length === 0;

  // Count how many parts across ALL areas already have a colour applied.
  const totalAssignedGlobal = Object.keys(assignments || {}).length;

  // Local (this-area) progress for the collapsible "N of M coloured" line.
  const localTotal = partsCatalog.length;
  const localColoured = partsCatalog.filter((p) => (assignments || {})[p.slug]).length;

  return (
    <div className="bg-white rounded-3xl shadow-soft border border-brand-border p-5" data-testid="editing-panel">
      <div className="flex items-center gap-2 mb-3">
        <Paintbrush2 size={14} className="text-brand-teal" />
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted">
          {editingEmpty ? "Your progress" : "You're colouring"}
        </div>
      </div>

      {editingEmpty && totalAssignedGlobal === 0 && (
        <p className="text-sm text-brand-muted" data-testid="editing-panel-empty">
          Tap a part of your home above to start colouring.
        </p>
      )}

      {editingEmpty && totalAssignedGlobal > 0 && (
        <div data-testid="editing-panel-progress" className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-brand-ink">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-teal text-white">
              <Check size={12} strokeWidth={3} />
            </span>
            <span>
              <strong className="font-semibold">{totalAssignedGlobal}</strong> {totalAssignedGlobal === 1 ? "part" : "parts"} coloured so far
              {activeAreaObj && localTotal > 0 && (
                <span className="text-brand-muted"> · {localColoured} of {localTotal} in {activeAreaObj.display_name}</span>
              )}
              .
            </span>
          </div>
          <p className="text-[11px] text-brand-muted leading-relaxed">
            Ready when you are — hit <span className="font-semibold text-brand-ink">Render Scheme</span> up top to see it painted.
            Or tap another part above to change or add a colour.
          </p>
        </div>
      )}

      {!editingEmpty && (
        <>
          <ul className="space-y-1.5" data-testid="editing-panel-selected-list">
            {rows.map((p) => (
              <li key={p.slug} className="text-sm text-brand-ink flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-brand-ink" aria-hidden="true" />
                <span>{p.display_name}</span>
                {activeAreaObj && (
                  <span className="text-[10px] text-brand-muted">({activeAreaObj.display_name})</span>
                )}
              </li>
            ))}
          </ul>
          <div className="text-[11px] text-brand-muted mt-2" data-testid="editing-panel-count">
            {rows.length} {rows.length === 1 ? "part of your home" : "parts of your home"}
          </div>
          <div className="mt-4 pt-4 border-t border-brand-border flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-2xl border-2 border-white shadow-soft"
              style={{ background: currentColour?.color_hex || "repeating-linear-gradient(45deg,#e7e3d8,#e7e3d8 4px,#f5f1e8 4px,#f5f1e8 8px)" }}
            />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted">Current colour</div>
              <div className="text-sm text-brand-ink font-semibold truncate">
                {currentColour?.color_name || currentColour?.color_hex || "Not picked"}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
